import { useQuery } from "@tanstack/react-query";
import { Loader2, Eye, Download, Inbox, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { api, downloadFile } from "@/lib/api";
import { Button } from "@/components/ui/button";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

const RUN_STATUS = {
  completed: { label: "Completada", bg: "#D1FAE5", text: "#065F46", border: "#34D399" },
  validation_error: { label: "Error de columnas", bg: "#FEE2E2", text: "#991B1B", border: "#F87171" },
  processing: { label: "Procesando", bg: "#FEF3C7", text: "#92400E", border: "#FBBF24" },
};

export function HistoryTable({ onSelectRun, currentRunId }) {
  const { data, isLoading } = useQuery({
    queryKey: ["runs"],
    queryFn: async () => (await api.get("/recon/runs")).data,
  });
  const runs = data?.items || [];

  const downloadOriginal = (run) => async () => {
    try {
      await downloadFile(`/recon/runs/${run.id}/file`, run.file_name);
    } catch {
      toast.error("Archivo original no disponible");
    }
  };

  return (
    <div className="bg-white border border-[#E2E8F0] rounded-sm" data-testid="history-table">
      <div className="overflow-auto thin-scroll">
        <table className="w-full text-sm min-w-[820px]">
          <thead>
            <tr className="bg-slate-50 border-b border-[#E2E8F0] text-left">
              {["Archivo", "Cargado por", "Fecha", "Líneas", "Resumen", "Estado", ""].map((h) => (
                <th key={h} className="px-3 py-2.5 text-[11px] uppercase tracking-[0.05em] font-semibold text-slate-500 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={7} className="py-16 text-center text-slate-400"><Loader2 className="animate-spin inline" size={20} /></td></tr>
            ) : runs.length === 0 ? (
              <tr><td colSpan={7} className="py-16 text-center text-slate-400"><Inbox className="inline mb-2" size={26} /><div className="text-sm">Aún no hay cargas</div></td></tr>
            ) : (
              runs.map((run) => {
                const st = RUN_STATUS[run.status] || RUN_STATUS.processing;
                const su = run.summary;
                return (
                  <tr key={run.id} className={`border-b border-slate-100 hover:bg-slate-50 ${run.id === currentRunId ? "bg-blue-50/40" : ""}`} data-testid={`run-row-${run.id}`}>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        <FileSpreadsheet size={15} className="text-[#2563EB] shrink-0" />
                        <span className="font-mono text-slate-800 max-w-[220px] truncate">{run.file_name}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-slate-600 max-w-[160px] truncate">{run.uploaded_by || "—"}</td>
                    <td className="px-3 py-2.5 text-slate-500 font-mono text-xs whitespace-nowrap">{fmtDate(run.uploaded_at)}</td>
                    <td className="px-3 py-2.5 font-mono text-slate-700">{run.total_rows ?? 0}</td>
                    <td className="px-3 py-2.5">
                      {su ? (
                        <div className="flex items-center gap-2 font-mono text-xs">
                          <span className="text-emerald-700">{su.conciliado}✓</span>
                          <span className="text-red-700">{su.diferencia}Δ</span>
                          <span className="text-amber-700">{su.pendiente_storis}⧖</span>
                        </div>
                      ) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-3 py-2.5">
                      <span className="inline-flex items-center rounded-sm border px-2 py-0.5 text-[11px] font-semibold"
                        style={{ background: st.bg, color: st.text, borderColor: st.border }}>{st.label}</span>
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-1 justify-end">
                        {run.status === "completed" && (
                          <Button size="sm" variant="outline" data-testid={`view-run-${run.id}`}
                            onClick={() => onSelectRun(run.id)} className="h-7 rounded-sm text-xs gap-1">
                            <Eye size={13} /> Ver
                          </Button>
                        )}
                        {run.storage_path && (
                          <Button size="sm" variant="ghost" data-testid={`download-run-${run.id}`}
                            onClick={downloadOriginal(run)} className="h-7 w-7 p-0 rounded-sm">
                            <Download size={14} />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
